<template>
  <div class="mr-5">
    <div class="card">
      <div class="card-body">
        <!-- Enhanced Filters Section -->
        <div class="filters-section">
          <div class="filters-header">
            <h4>
              <a-icon type="filter" class="filter-icon" />
              {{ $t("all.filtresEtRecherche") }}
            </h4>
            <div class="action-button-group">
              <a-button
                icon="reload"
                @click="resetFilters"
                class="action-button"
              >
                {{ $t("action.reinitialiser") }}
              </a-button>
              <a-button
                icon="file-pdf"
                type="primary"
                @click="generatePdf"
                :loading="printLoading"
                :disabled="printLoading"
                class="action-button"
              >
                {{ $t("action.imprimer") }}
              </a-button>

              <json-csv
                class="csv-button"
                :labels="csvLabels"
                :data="csvToExport"
                :name="'listeReglementAnnulé.csv'"
              >
                <a-icon type="file-excel" class="text-success action-icon" />
                CSV
              </json-csv>
            </div>
          </div>

          <a-row :gutter="[16, 16]" class="align-items-end">
            <!-- Search Input -->
            <a-col :xs="24" :sm="8" :md="8" :lg="8">
              <div class="filter-item">
                <label class="filter-label">{{
                  $t("tresorerie.recherche")
                }}</label>
                <a-input
                  v-model="searchQuery"
                  :placeholder="$t('personnel.chercher')"
                  @input="handleSearchInput"
                  allow-clear
                  class="search-input"
                >
                  <a-icon slot="prefix" type="search" />
                </a-input>
              </div>
            </a-col>

            <!-- Type Filter -->
            <a-col :xs="24" :sm="8" :md="8" :lg="8">
              <div class="filter-item">
                <label class="filter-label">{{ $t("tresorerie.type") }}</label>
                <a-select
                  v-model="typeFilter"
                  :placeholder="$t('tresorerie.selectionnerType')"
                  @change="handleFilter"
                  allow-clear
                  class="filter-select"
                >
                  <a-select-option value="Annulation">
                    <a-icon type="close-circle" class="status-icon inactive" />
                    {{ $t("tresorerie.annulation") }}
                  </a-select-option>
                  <a-select-option value="Avoir">
                    <a-icon type="file-text" class="type-icon text-warning" />
                    {{ $t("tresorerie.avoir") }}
                  </a-select-option>
                </a-select>
              </div>
            </a-col>

            <!-- Client Name Filter -->
            <a-col :xs="24" :sm="8" :md="8" :lg="8">
              <div class="filter-item">
                <label class="filter-label">{{
                  $t("tresorerie.nomClient")
                }}</label>
                <a-input
                  v-model="clientNameFilter"
                  :placeholder="$t('tresorerie.filtrerParNom')"
                  @input="handleClientNameFilter"
                  allow-clear
                  class="search-input"
                >
                  <a-icon slot="prefix" type="user" />
                </a-input>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- Enhanced Table -->
        <div class="table-container">
          <a-table
            :loading="tableLoading"
            :rowKey="'_id'"
            :columns="columns"
            :data-source="data"
            :pagination="pagination"
            :scroll="{ x: 'max-content' }"
            @change="tableChanged"
            size="middle"
            :row-class-name="getRowClassName"
            :bordered="false"
            :expandedRowRender="expandedRowRender"
          >
            <template slot="clientName" slot-scope="text">
              <span class="client-name">{{ text || "--" }}</span>
            </template>

            <template slot="date" slot-scope="text">
              <span class="date-cell">
                {{ text ? moment(text).format("DD/MM/YYYY") : "--" }}
              </span>
            </template>

            <template slot="money" slot-scope="text">
              <span class="amount-cell">
                {{ formatAmount(text) }}
              </span>
            </template>

            <template slot="allTypeReglement" slot-scope="text">
              <div class="type-reglement">
                <a-tag
                  v-for="(item, index) in text"
                  :key="index"
                  color="blue"
                  style="margin: 2px"
                  class="status-tag"
                >
                  {{ item }}
                </a-tag>
              </div>
            </template>

            <template slot="type" slot-scope="text">
              <a-tag :color="getTypeColor(text)" class="type-tag">
                {{ translateType(text) }}
              </a-tag>
            </template>

            <template slot="userName" slot-scope="text">
              <span class="text-danger">
                <a-icon type="user" class="action-icon text-danger" />
                {{ text || "--" }}
              </span>
            </template>

            <div
              slot="filterDropdown"
              slot-scope="{
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
                column,
              }"
              class="padding-8"
            >
              <a-input
                v-ant-ref="(c) => (searchInput = c)"
                :placeholder="`${$t('personnel.chercher')} ${column.title}`"
                :value="selectedKeys[0]"
                style="width: 188px; margin-bottom: 8px; display: block"
                @change="
                  (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
                "
                @pressEnter="
                  () => handleSearch(selectedKeys, confirm, column.dataIndex)
                "
              />
              <a-button
                type="primary"
                icon="search"
                size="small"
                style="width: 90px; margin-right: 8px"
                @click="
                  () => handleSearch(selectedKeys, confirm, column.dataIndex)
                "
                >{{ $t("paiement.chercher") }}</a-button
              >
              <a-button
                size="small"
                style="width: 90px"
                @click="() => handleReset(clearFilters)"
                >{{ $t("action.reinitialiser") }}</a-button
              >
            </div>
            <a-icon
              slot="filterIcon"
              slot-scope="filtered"
              type="search"
              :style="{ color: filtered ? '#108ee9' : undefined }"
            />
            <template
              slot="customRender"
              slot-scope="text, record, index, column"
            >
              <span v-if="searchText && searchedColumn === column.dataIndex">
                <template
                  v-for="(fragment, i) in text
                    .toString()
                    .split(
                      new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i')
                    )"
                >
                  <mark
                    v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                    :key="i"
                    class="highlight"
                    >{{ fragment }}</mark
                  >
                  <template v-else>{{ fragment }}</template>
                </template>
              </span>
              <template v-else>{{ text }}</template>
            </template>
          </a-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState } from "vuex";
import apiClient from "@/services/axios";
import moment from "moment";
import JsonCsv from "vue-json-csv";
import {
  generateCancellationListPdf,
  generateSingleCancellationPdf,
} from "./pdfGenerator";
import { prepareCsvExport, downloadCsvFile } from "./csvExporter";
import { formatCurrency } from "@/utilities/countrySpecific";

export default {
  components: {
    JsonCsv,
  },
  computed: {
    ...mapState(["settings"]),
    csvToExport() {
      const csvExport = prepareCsvExport(this.data);
      return csvExport.data;
    },
    csvLabels() {
      const csvExport = prepareCsvExport(this.data);
      return csvExport.labels;
    },
    columns() {
      return [
        {
          title: this.$t("recette.numSerie"),
          dataIndex: "serial",
          key: "serial",
          scopedSlots: {
            customRender: "serial",
          },
          width: 120,
        },
        {
          title: this.$t("recette.nomParent"),
          dataIndex: "clientName",
          key: "clientName",
          scopedSlots: {
            customRender: "clientName",
          },
          width: 150,
        },
        {
          title: this.$t("tresorerie.type"),
          dataIndex: "type",
          key: "type",
          scopedSlots: {
            customRender: "type",
          },
          width: 100,
        },
        {
          title: this.$t("recette.dateAnnulation"),
          dataIndex: "date",
          key: "date",
          scopedSlots: {
            customRender: "date",
          },
          width: 120,
        },
        {
          title: this.$t("recette.montantTTC"),
          dataIndex: "money",
          key: "money",
          scopedSlots: {
            customRender: "money",
          },
          width: 120,
        },
        {
          title: this.$t("recette.typeReglement"),
          dataIndex: "allTypeReglement",
          key: "allTypeReglement",
          scopedSlots: {
            customRender: "allTypeReglement",
          },
          width: 200,
        },
        {
          title: this.$t("recette.cancelledBy"),
          dataIndex: "userName",
          key: "userName",
          scopedSlots: {
            customRender: "userName",
          },
          width: 120,
        },
      ];
    },
  },
  async created() {
    await this.fetchData();
  },
  data() {
    return {
      searchQuery: "",
      typeFilter: undefined,
      clientNameFilter: "",
      searchTimeout: null,
      clientNameTimeout: null,
      printLoading: false,
      tableLoading: true,
      data: [],
      filtredTable: [],
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} ${this.$t("tresorerie.sur")} ${total} ${this.$t("tresorerie.elements")}`,
        pageSizeOptions: ["10", "20", "50", "100"],
        size: "default",
        position: "bottom",
        style: { marginTop: "16px" },
      },
    };
  },
  methods: {
    async fetchData(page = 1, pageSize = 10) {
      try {
        this.tableLoading = true;

        const params = {
          page,
          limit: pageSize,
        };

        // Add search query
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        // Add type filter
        if (this.typeFilter) {
          params.type = this.typeFilter;
        }

        // Add client name filter
        if (this.clientNameFilter) {
          params.clientName = this.clientNameFilter;
        }

        const response = await apiClient.get("/compV3/cancellation", {
          params,
        });

        this.data = response.data.data;
        this.filtredTable = this.data.map((item) => ({ ...item }));

        // Update pagination
        this.pagination.current = response.data.pagination.currentPage;
        this.pagination.total = response.data.pagination.totalItems;
        this.pagination.pageSize = response.data.pagination.itemsPerPage;

        this.tableLoading = false;
      } catch (error) {
        console.error("Error fetching data:", error);
        this.tableLoading = false;
        this.$message.error(this.$t("tresorerie.erreurChargementDonnees"));
      }
    },

    handleSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.pagination.current = 1;
        this.fetchData();
      }, 500);
    },

    handleClientNameFilter() {
      // Clear existing timeout
      if (this.clientNameTimeout) {
        clearTimeout(this.clientNameTimeout);
      }

      // Set new timeout for debounced search
      this.clientNameTimeout = setTimeout(() => {
        this.pagination.current = 1;
        this.fetchData();
      }, 500);
    },

    handleFilter() {
      this.pagination.current = 1;
      this.fetchData();
    },

    resetFilters() {
      // Clear timeouts
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      if (this.clientNameTimeout) {
        clearTimeout(this.clientNameTimeout);
      }

      this.searchQuery = "";
      this.typeFilter = undefined;
      this.clientNameFilter = "";
      this.pagination.current = 1;
      this.fetchData();
    },

    tableChanged(pagination, filters, sorter, extra) {
      this.filtredTable = extra.currentDataSource;

      // Handle pagination change
      if (
        pagination.current !== this.pagination.current ||
        pagination.pageSize !== this.pagination.pageSize
      ) {
        this.fetchData(pagination.current, pagination.pageSize);
      }
    },

    expandedRowRender(record) {
      return this.$createElement("p", [
        this.$t("punition.raison") + " : " + (record.note || "--"),
      ]);
    },

    getTypeColor(type) {
      const colors = {
        Annulation: "red",
        Avoir: "orange",
      };
      return colors[type] || "default";
    },

    formatAmount(amount) {
      return formatCurrency(amount || 0);
    },
    
    translateType(type) {
      const typeMap = {
        Annulation: this.$t("tresorerie.annulation"),
        Avoir: this.$t("tresorerie.avoir"),
      };
      return typeMap[type] || type;
    },

    getRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },

    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },

    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },

    moment,

    generatePdf() {
      // Track PDF generation event
      this.$gtag.event("Imp listeReglementAnnulé", {
        event_category: "Impression PDF",
        event_label: "comptabilite:listeReglementAnnulé",
        value: 1,
      });

      this.printLoading = true;

      try {
        // Prepare filters object
        const filters = {
          type: this.typeFilter,
          clientName: this.clientNameFilter,
          search: this.searchQuery,
        };

        // Generate PDF using the refactored utility
        const result = generateCancellationListPdf({
          data: this.filtredTable,
          settings: this.settings,
          filters: filters,
          t: this.$t.bind(this),
          locale: this.$i18n.locale
        });

        if (result.success) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$message.error(this.$t("tresorerie.pdfGenerationError"));
      } finally {
        this.printLoading = false;
      }
    },

    generateSinglePdf(cancellation) {
      try {
        const result = generateSingleCancellationPdf(
          cancellation,
          this.settings,
          this.$t.bind(this),
          this.$i18n.locale
        );

        if (result.success) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        console.error("Error generating single cancellation PDF:", error);
        this.$message.error(this.$t("tresorerie.pdfGenerationError"));
      }
    },

    downloadCsvDirect() {
      try {
        const filename = `reglements-annules_${moment().format(
          "YYYY-MM-DD_HH-mm"
        )}.csv`;
        const filters = {
          type: this.typeFilter,
          clientName: this.clientNameFilter,
          search: this.searchQuery,
        };

        const result = downloadCsvFile(this.filtredTable, filename, {
          filters,
        });

        if (result.success) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        console.error("Error downloading CSV:", error);
        this.$message.error(this.$t("tresorerie.erreurTelechargementCSV"));
      }
    },
  },
};
</script>

<style>
@import "../../common.css";
</style>
